import { useMemo, useState } from 'react'

import { Alert, Button, Link } from '@atoms'
import { Progress } from '@components/atoms/Progress'
import { PageLayout } from '@components/layouts/PageLayout'
import EmptyState from '@components/molecules/EmptyState'
import { SelectField } from '@components/molecules/SelectField'
import cn from '@components/utils/cn'
import Icon from '@components/utils/Icon'

import { DesignExplorer, SegControl } from '../components/DesignExplorer'
import { FAILURES_ID } from '../components/imports/ImportSummary'
import {
  ALL_GROUPS,
  HISTORY_RUNS,
  type ImportRun,
  type ImportType,
  isSettled,
  summaryPath,
  type RunStatus,
  SHORT_STATUS_LABELS,
  TYPE_LABELS,
} from '../components/imports/sampleData'
import { AlertText, inlineLinkClasses, ReviewedBadge, StatusBadge } from '../components/imports/ui'

/**
 * Admin imports history (design-phase prototype, story 02 / P0.1)
 *
 * `/imports` on Inertia (P3.2): every run of every type, newest first, with
 * the results in the list so an admin rarely needs the summary. A run that is
 * still working shows its progress instead of empty counts.
 */

type ResultsLayout = 'numbers' | 'bar'

const STATUS_FILTERS: RunStatus[] = ['working', 'queued', 'completed', 'completed_with_failures', 'failed']

/** The optional review extension adds a "Reviewed" status filter, which RunStatus does not have. */
type StatusFilter = RunStatus | 'reviewed'

/** With the review extension on, this sample run has been marked as reviewed. */
const REVIEWED_RUN_IDS = new Set([38])

function FilterBar({
  type,
  group,
  status,
  onType,
  onGroup,
  onStatus,
  reviewExtension,
}: {
  type: ImportType | null
  group: string | null
  status: StatusFilter | null
  onType: (value: ImportType | null) => void
  onGroup: (value: string | null) => void
  onStatus: (value: StatusFilter | null) => void
  reviewExtension: boolean
}) {
  return (
    <div className="tw:flex tw:flex-wrap tw:items-end tw:gap-3">
      <div className="tw:w-full tw:sm:w-44">
        <SelectField<ImportType>
          hideLabel
          name="type"
          label="Type"
          emptyOptionLabel="All types"
          options={(Object.keys(TYPE_LABELS) as ImportType[]).map((value) => ({ value, label: TYPE_LABELS[value] }))}
          value={type}
          onValueChange={onType}
        />
      </div>
      <div className="tw:w-full tw:sm:w-56">
        <SelectField
          hideLabel
          search
          name="group"
          label="Group"
          emptyOptionLabel="All groups"
          options={ALL_GROUPS.map((name) => ({ value: name, label: name }))}
          value={group}
          onValueChange={onGroup}
        />
      </div>
      <div className="tw:w-full tw:sm:w-64">
        <SelectField<StatusFilter>
          hideLabel
          name="status"
          label="Status"
          emptyOptionLabel="All statuses"
          options={[
            ...STATUS_FILTERS.map((value) => ({ value, label: SHORT_STATUS_LABELS[value] })),
            ...(reviewExtension ? [{ value: 'reviewed' as const, label: 'Reviewed' }] : []),
          ]}
          value={status}
          onValueChange={onStatus}
        />
      </div>
    </div>
  )
}

/** Counts in the list, with the failed and skipped counts jumping to the summary's failures table. */
function ResultsCell({ run, layout }: { run: ImportRun; layout: ResultsLayout }) {
  const { created, updated, failed, skipped } = run.counts
  const failuresHref = `${summaryPath(run)}#${FAILURES_ID}`
  const settled = isSettled(run.status)

  if (!settled) {
    const percent = Math.round((run.processedRowCount / run.submittedRowCount) * 100)
    return (
      <div className="tw:flex tw:w-48 tw:flex-col tw:gap-1">
        <Progress size="compact" value={percent} aria-label={`Rows processed for import ${run.id}`} />
        <span className="tw:text-xs tw:tabular-nums tw:text-gray-cool-600">
          {run.status === 'queued'
            ? `${run.submittedRowCount.toLocaleString()} rows waiting`
            : `${run.processedRowCount.toLocaleString()} of ${run.submittedRowCount.toLocaleString()} rows`}
        </span>
      </div>
    )
  }

  const total = created + updated + failed + skipped
  const segments = [
    { value: created, label: 'created', color: 'tw:bg-success-600' },
    { value: updated, label: 'updated', color: 'tw:bg-primary-500' },
    { value: failed, label: 'failed', color: 'tw:bg-error-600' },
    { value: skipped, label: 'skipped', color: 'tw:bg-warning-600' },
  ]

  return (
    <div className="tw:flex tw:flex-col tw:gap-1.5">
      {layout === 'bar' && (
        <div
          role="img"
          aria-label={segments.map((segment) => `${segment.value} ${segment.label}`).join(', ')}
          className="tw:flex tw:h-1.5 tw:w-48 tw:overflow-hidden tw:rounded-full tw:bg-gray-cool-100"
        >
          {segments.map(
            (segment) =>
              segment.value > 0 && (
                <span
                  key={segment.label}
                  className={cn('tw:h-full', segment.color)}
                  style={{ width: `${(segment.value / total) * 100}%` }}
                />
              )
          )}
        </div>
      )}
      <dl className="tw:flex tw:flex-wrap tw:gap-x-3 tw:gap-y-0.5 tw:text-xs tw:tabular-nums">
        <ResultCount value={created} label="created" />
        <ResultCount value={updated} label="updated" />
        <ResultCount value={failed} label="failed" href={failuresHref} tone="error" />
        <ResultCount value={skipped} label="skipped" href={failuresHref} tone="warning" />
      </dl>
    </div>
  )
}

function ResultCount({
  value,
  label,
  href,
  tone,
}: {
  value: number
  label: string
  href?: string
  tone?: 'error' | 'warning'
}) {
  // error-600 and warning-700 are the lightest shades that pass 4.5:1 on white.
  const toneClass =
    tone === 'error' ? 'tw:text-error-600' : tone === 'warning' ? 'tw:text-warning-700' : 'tw:text-gray-cool-900'
  const number = (
    <span className={cn('tw:font-medium', value === 0 ? 'tw:text-gray-cool-400' : toneClass)}>
      {value.toLocaleString()}
    </span>
  )

  return (
    <div className="tw:flex tw:items-baseline tw:gap-1">
      <dd className="tw:m-0">
        {href && value > 0 ? (
          <a href={href} className={cn(inlineLinkClasses, toneClass)} aria-label={`${value} ${label}, see which rows`}>
            {number}
          </a>
        ) : (
          number
        )}
      </dd>
      <dt className={cn(value === 0 ? 'tw:text-gray-cool-400' : 'tw:text-gray-cool-600')}>{label}</dt>
    </div>
  )
}

/** The date, with the time and who imported it underneath in smaller text, to keep the table narrow. */
function Submitted({ run }: { run: ImportRun }) {
  const [date, time] = run.submittedAt.split(', ')
  return (
    <span className="tw:flex tw:flex-col tw:gap-0.5">
      <span className="tw:text-gray-cool-900">{date}</span>
      <span className="tw:text-xs tw:text-gray-cool-600">
        {time} by {run.importer}
      </span>
    </span>
  )
}

const HEADERS = ['Type', 'Group', 'Status', 'Submitted', 'Results', '']

function RunsTable({
  runs,
  layout,
  isReviewed,
}: {
  runs: ImportRun[]
  layout: ResultsLayout
  isReviewed: (run: ImportRun) => boolean
}) {
  return (
    <div className="tw:overflow-x-auto tw:rounded-xl tw:border tw:border-gray-200 tw:bg-white tw:shadow-xs">
      <table className="tw:min-w-full tw:border-collapse tw:text-sm">
        <caption className="tw:sr-only">Import runs, newest first</caption>
        <thead>
          <tr className="tw:bg-gray-cool-50 tw:text-left tw:text-xs tw:text-gray-cool-600">
            {HEADERS.map((header, index) => (
              <th
                key={header || 'summary'}
                scope="col"
                className={cn(
                  'tw:whitespace-nowrap tw:border-b tw:border-gray-200 tw:py-2.5 tw:font-medium',
                  index === 0 ? 'tw:px-5' : 'tw:px-3'
                )}
              >
                {header || <span className="tw:sr-only">Summary</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr
              key={run.id}
              className="tw:border-b tw:border-gray-100 tw:last:border-0 tw:odd:bg-white tw:even:bg-gray-cool-50/40"
            >
              <td className="tw:px-5 tw:py-3">
                <span className="tw:flex tw:flex-col tw:gap-0.5">
                  <span className="tw:whitespace-nowrap tw:font-medium tw:text-gray-cool-900">
                    {TYPE_LABELS[run.type]}
                  </span>
                  {run.event && (
                    <span className="tw:max-w-44 tw:text-xs tw:text-gray-cool-600">
                      {run.event}
                      {run.markAsAttended && ' · marked attended'}
                    </span>
                  )}
                </span>
              </td>
              <td className="tw:px-3 tw:py-3">
                <span className="tw:text-gray-cool-900">{run.group}</span>
              </td>
              <td className="tw:whitespace-nowrap tw:px-3 tw:py-3">
                {isReviewed(run) ? <ReviewedBadge /> : <StatusBadge status={run.status} short />}
              </td>
              <td className="tw:whitespace-nowrap tw:px-3 tw:py-3">
                <Submitted run={run} />
              </td>
              <td className="tw:px-3 tw:py-3">
                <ResultsCell run={run} layout={layout} />
              </td>
              <td className="tw:whitespace-nowrap tw:px-3 tw:py-3 tw:text-right">
                <Link href={summaryPath(run)} rank="secondary" size="sm" ariaLabel={`View import ${run.id}`}>
                  View
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const ImportsHistoryPreview = () => {
  const [type, setType] = useState<ImportType | null>(null)
  const [group, setGroup] = useState<string | null>(null)
  const [status, setStatus] = useState<StatusFilter | null>(null)
  const [layout, setLayout] = useState<ResultsLayout>('numbers')
  const [showEmpty, setShowEmpty] = useState(false)
  const [reviewExtension, setReviewExtension] = useState(false)

  const isReviewed = (run: ImportRun) => reviewExtension && REVIEWED_RUN_IDS.has(run.id)
  const matchesStatus = (run: ImportRun) =>
    status === null || (status === 'reviewed' ? isReviewed(run) : run.status === status && !isReviewed(run))

  const filtered = useMemo(
    () =>
      (showEmpty ? [] : HISTORY_RUNS).filter(
        (run) => (type === null || run.type === type) && (group === null || run.group === group) && matchesStatus(run)
      ),
    [type, group, status, showEmpty, reviewExtension]
  )

  const filtering = type !== null || group !== null || status !== null
  const clearFilters = () => {
    setType(null)
    setGroup(null)
    setStatus(null)
  }

  const workingCount = HISTORY_RUNS.filter((run) => !isSettled(run.status)).length

  return (
    <PageLayout.Root>
      <PageLayout.Header
        icon="upload-cloud-02"
        heading="Imports"
        subtitle="Every supporter and RSVP import on this instance, and how each one went."
        actions={
          <Button leadingIcon="plus-circle" onClick={() => {}}>
            New import
          </Button>
        }
      />
      <PageLayout.Body>
        <div className="tw:mx-auto tw:flex tw:max-w-7xl tw:flex-col tw:gap-4">
          <FilterBar
            type={type}
            group={group}
            status={status}
            onType={setType}
            onGroup={setGroup}
            onStatus={setStatus}
            reviewExtension={reviewExtension}
          />

          {/* Always on the page so a filter result is announced. */}
          <div role="status">
            {filtering && filtered.length > 0 && (
              <Alert type="info" size="sm" icon="search-lg" className="tw:text-sm">
                <AlertText>
                  Showing {filtered.length} of {HISTORY_RUNS.length} imports.{' '}
                  <button type="button" onClick={clearFilters} className={inlineLinkClasses}>
                    Clear filters
                  </button>
                </AlertText>
              </Alert>
            )}
          </div>

          {filtered.length === 0 ? (
            <EmptyState
              iconType="upload-cloud-02"
              headline={filtering ? 'No imports match these filters' : 'No imports yet'}
              subtext={
                filtering
                  ? 'Try a different type, group or status.'
                  : 'When an admin or a captain imports a file, it is listed here with its results.'
              }
            >
              {filtering ? (
                <Button rank="secondary" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : (
                <Button leadingIcon="plus-circle" onClick={() => {}}>
                  New import
                </Button>
              )}
            </EmptyState>
          ) : (
            <RunsTable runs={filtered} layout={layout} isReviewed={isReviewed} />
          )}

          {workingCount > 0 && !showEmpty && (
            <p className="tw:flex tw:items-center tw:gap-2 tw:text-xs tw:text-gray-cool-600">
              <Icon type="refresh" className="tw:h-3.5 tw:w-3.5" aria-hidden />
              {workingCount} {workingCount === 1 ? 'import is' : 'imports are'} still running. This list refreshes on
              its own.
            </p>
          )}
        </div>
      </PageLayout.Body>

      <DesignExplorer>
        <SegControl
          label="Results column"
          value={layout}
          onChange={(value) => setLayout(value)}
          options={[
            { value: 'numbers', label: 'Numbers' },
            { value: 'bar', label: 'Bar and numbers' },
          ]}
        />
        <SegControl
          label="Data"
          value={showEmpty ? 'empty' : 'sample'}
          onChange={(value) => setShowEmpty(value === 'empty')}
          options={[
            { value: 'sample', label: 'Sample runs' },
            { value: 'empty', label: 'No runs yet' },
          ]}
        />
        <SegControl
          label="Mark as reviewed (optional extension)"
          value={reviewExtension ? 'on' : 'off'}
          onChange={(value) => {
            setReviewExtension(value === 'on')
            if (status === 'reviewed') setStatus(null)
          }}
          options={[
            { value: 'off', label: 'Off' },
            { value: 'on', label: 'On' },
          ]}
        />
      </DesignExplorer>
    </PageLayout.Root>
  )
}

export default ImportsHistoryPreview
